/**
 * The presence vocabulary: four statuses and their icon+text presentation, plus the
 * neutral trust label. Task PW1 (pillar web).
 *
 * A module of its own, next to `attendance/status.ts` rather than inside
 * `screens/PresenceScreen.tsx`, for the same reason that file gives: the table is the
 * single door a caller goes through to render a status, so no place on this screen can
 * invent a colour with no words or the wrong glyph.
 *
 * ── Why these four icons, and why none of them is a celebration ────────────────
 *
 * `working_onsite` and `working_remote` reuse the transport marks Task 42 already
 * drew for the timer (`play`) split from `on_break` (`pause`) and `offline`
 * (`square`) — not a coincidence: `src/modules/presence/presence.ts`'s own header
 * says presence's two working statuses are anchored to an open (`running`) task
 * timer, `on_break` to a `paused` one, and `offline` to no open timer at all. Reusing
 * the timer's own silhouettes keeps that fact visible in the icon rather than only in
 * the backend's derivation.
 *
 * Every severity below is `low` or `info`, never `medium` or above: this is a
 * coordination-status read, not an accusation, and spec 01 §4.1.4's "no gamifying
 * surveillance" rule that keeps `attendance.status.on_time` at `low` (a real state,
 * not a reward) applies here with the same weight in both directions — `offline`
 * during working hours is not rendered as a warning either, because raw device
 * liveness is deliberately not part of this status (the backend's own guardrail).
 */

import type { MessageKey } from '../i18n/messages';
import type { BadgeTone } from '../states/Badge';
import type { IconName, Severity } from '../states/icons';
import type { PresenceStatus, PresenceTrust } from '../api/presence';

/** One row of `web/shapes/presence.md` §6, and of `DESIGN.md` §4 rule 4. */
export interface PresenceStatusPresentation {
  readonly severity: Severity;
  readonly icon: IconName;
  readonly labelKey: MessageKey;
  readonly helpKey: MessageKey;
  /** How loudly to paint it. See the note under the table. */
  readonly tone: BadgeTone;
}

/**
 * The table. Four entries for four values, exhaustive over `PresenceStatus` by its
 * type — a value the server invents cannot fall through to a colour with no words,
 * because there is no fallback branch anywhere that reads this map.
 */
export const PRESENCE_STATUS_PRESENTATION: Readonly<
  Record<PresenceStatus, PresenceStatusPresentation>
> = {
  working_onsite: {
    severity: 'low',
    icon: 'circle-check',
    labelKey: 'presence.status.working_onsite',
    helpKey: 'presence.status.help.working_onsite',
    tone: 'neutral',
  },
  working_remote: {
    severity: 'low',
    icon: 'play',
    labelKey: 'presence.status.working_remote',
    helpKey: 'presence.status.help.working_remote',
    tone: 'neutral',
  },
  on_break: {
    severity: 'info',
    icon: 'pause',
    labelKey: 'presence.status.on_break',
    helpKey: 'presence.status.help.on_break',
    tone: 'neutral',
  },
  offline: {
    severity: 'info',
    icon: 'square',
    labelKey: 'presence.status.offline',
    helpKey: 'presence.status.help.offline',
    tone: 'neutral',
  },
};

/*
 * ── Why every row above is `tone: 'neutral'` ─────────────────────────────────
 *
 * The header of this file already committed to it: "every severity below is `low` or
 * `info`, never `medium` or above … this is a coordination-status read, not an
 * accusation". Painting four never-alarming values in four different washes spent
 * the colour channel on a column where nothing is ever wrong, and the finish review
 * saw the result — a board of six people where every single row wore a coloured chip
 * and none of them meant anything. `severity` is kept because it is still the
 * underlying classification (`data-severity` carries it), but the paint is not.
 *
 * The place presence **does** spend colour is the exception list on `/overview`,
 * where a row is there precisely because somebody has to act on it.
 */

/**
 * The trust flag's own words — always `info` severity, whatever the value, which is
 * the mechanical form of "never an accusation" (`openapi.yaml`'s own description of
 * `PresenceWhere.trust`): `suspect` does not read louder than `trusted` here.
 */
export const PRESENCE_TRUST_LABEL: Readonly<Record<PresenceTrust, MessageKey>> = {
  trusted: 'presence.trust.trusted',
  suspect: 'presence.trust.suspect',
  unverified: 'presence.trust.unverified',
};
